// src/contexts/AuthContext.tsx
import React, { createContext, useContext, useEffect, useState, ReactNode } from 'react';
import {
  User,
  signInWithEmailAndPassword,
  createUserWithEmailAndPassword,
  signOut,
  onAuthStateChanged,
  sendEmailVerification,
  UserCredential
} from 'firebase/auth';
import { auth } from '../config/firebase';

interface AuthContextType {
  currentUser: User | null;
  signIn: (credentials: { email: string; password: string }) => Promise<{ data?: UserCredential; error?: any }>;
  signUp: (credentials: { email: string; password: string }) => Promise<{ data?: UserCredential; error?: any }>;
  signOut: () => Promise<void>;
  loading: boolean;
}

const AuthContext = createContext<AuthContextType | undefined>(undefined);

export const useAuth = (): AuthContextType => {
  const context = useContext(AuthContext);
  if (context === undefined) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};

interface AuthProviderProps {
  children: ReactNode;
}

export const AuthProvider: React.FC<AuthProviderProps> = ({ children }) => {
  const [currentUser, setCurrentUser] = useState<User | null>(null);
  const [loading, setLoading] = useState(true);

  const signIn = async (credentials: { email: string; password: string }) => {
    try {
      const result = await signInWithEmailAndPassword(auth, credentials.email, credentials.password);
      return { data: result };
    } catch (error: any) {
      return { error: { message: error.message } };
    }
  };

  const signUp = async (credentials: { email: string; password: string }) => {
    try {
      const result = await createUserWithEmailAndPassword(auth, credentials.email, credentials.password);
      
      // Send email verification
      if (result.user) {
        await sendEmailVerification(result.user);
      }
      
      // FIX: Return the 'UserCredential' object directly in the 'data' property.
      // The original code was wrapping it in another object, causing a type mismatch.
      return { data: result };
    } catch (error: any) {
      return { error: { message: error.message } };
    }
  };

  const logout = async () => {
    await signOut(auth);
  };

  useEffect(() => {
    const unsubscribe = onAuthStateChanged(auth, (user) => {
      setCurrentUser(user);
      setLoading(false);
    });

    return unsubscribe;
  }, []);

  const value: AuthContextType = {
    currentUser,
    signIn,
    signUp,
    signOut: logout,
    loading
  };

  return (
    <AuthContext.Provider value={value}>
      {!loading && children}
    </AuthContext.Provider>
  );
};